import { useMemo, useState } from 'react'
import styles from './KeySpecs.module.css'
import SpecIcon from './SpecIcon'
import SectionHeader from './SectionHeader'
import DistributionBars from './DistributionBars'
import type {
  CategoricalDistribution,
  DealHuntSpec,
  SpecDistribution,
  SpecDistributionsResponse,
} from 'lib/dealhunt/types'
import { formatSpecValue } from 'lib/dealhunt/url'

interface KeySpecsProps {
  specs: DealHuntSpec[]
  /** Per-spec class distributions (DealHunt /api/spec-distributions), pinned to the
   *  featured keys. Null → the cards render value-only (no chart), never blank. */
  specDistributions: SpecDistributionsResponse | null
}

function titleCase(input: string): string {
  return input
    .replace(/[._-]+/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase())
    .trim()
}

function round(n: number): number {
  return Math.round(n)
}

/** Presentation-only label for a categorical option: IP-style codes ("ip54") read as
 *  uppercase, plain words ("active") title-cased. Not a data contract — the distribution
 *  option values are normalized keys; the chip/legend just makes them presentable. */
function formatOption(value: string): string {
  if (value === 'Others') {
    return value
  }
  if (/\d/.test(value) && value.length <= 6) {
    return value.toUpperCase()
  }
  return value.charAt(0).toUpperCase() + value.slice(1)
}

const BAND_LABEL: Record<string, string> = {
  below_average: 'Below average',
  average: 'Average',
  above_average: 'Above average',
}

/** Standing pill for a card. Quantifiable → the class-relative `band` (amber below /
 *  green above); categorical → a neutral "Most common" when the product holds the modal
 *  option. Richer categorical standings ("Ahead of most") need the ordinal data DealHunt
 *  doesn't serve yet (see migration doc). Null → no pill. */
function standingPill(d: SpecDistribution): { label: string; tone: string } | null {
  if (d.kind === 'quantifiable') {
    if (!d.band) {
      return null
    }
    const TONE: Record<string, string> = {
      below_average: 'below',
      above_average: 'above',
      average: 'neutral',
    }
    return { label: BAND_LABEL[d.band], tone: TONE[d.band] }
  }
  return d.isMostCommon ? { label: 'Most common', tone: 'common' } : null
}

/** Neutral, factual one-liner derived from the distribution (band = vs class avg only). */
function caption(d: SpecDistribution, peers: string): string {
  if (d.kind === 'quantifiable') {
    const band = d.band ? `${BAND_LABEL[d.band].toLowerCase()} for ` : ''
    return `${band}${peers} — based on ${d.known} of ${d.classSize} we've cataloged.`
  }
  const option = Array.isArray(d.value) ? d.value[0] : d.value
  const match = d.options.find(o => o.value === option)
  const pct = match ? round(match.share * 100) : null
  if (option === null || pct === null) {
    return `Based on ${d.known} of ${d.classSize} ${peers} we've cataloged.`
  }
  const label = formatOption(option)
  if (d.isMostCommon) {
    return `${label} is the most common — on ${pct}% of the ${d.known} ${peers} we've cataloged (of ${d.classSize}).`
  }
  return `${label} appears on ${pct}% of the ${d.known} ${peers} we've cataloged (of ${d.classSize}).`
}

/** Selected option values for a categorical distribution (set-valued specs included). */
function selectedSet(value: string | string[] | null): Set<string> {
  if (Array.isArray(value)) {
    return new Set(value)
  }
  return new Set(value ? [value] : [])
}

/**
 * Categorical chart: a share-of-class segment bar + a legend. Segment width ∝ how common
 * each option is in the class (frequency), the product's option highlighted. (The design's
 * ordinal "capability ladder" + "better than N%" copy needs value-ordering DealHunt doesn't
 * expose yet — see the migration doc; this is the data-honest rendering.)
 */
type CategoricalOption = CategoricalDistribution['options'][number]

const LEGEND_LIMIT = 4

/** Collapsed legend: the most-common options (they come share-sorted) capped at `limit`,
 *  but always keeping the product's own option visible even when it's down the long tail. */
function collapseLegend(
  options: CategoricalOption[],
  selected: Set<string>,
  limit: number
): CategoricalOption[] {
  if (options.length <= limit) {
    return options
  }
  const head = options.slice(0, limit)
  if (head.some(o => !o.isOther && selected.has(o.value))) {
    return head
  }
  const own = options.find(o => !o.isOther && selected.has(o.value))
  return own ? [...options.slice(0, limit - 1), own] : head
}

const CategoricalChart: React.VFC<{ d: CategoricalDistribution }> = ({ d }) => {
  const selected = selectedSet(d.value)
  const [showAll, setShowAll] = useState(false)
  const visible = showAll ? d.options : collapseLegend(d.options, selected, LEGEND_LIMIT)
  const hidden = d.options.length - visible.length
  return (
    <>
      <div className={styles.segbar}>
        {d.options.map((o, i) => (
          <span
            // index key so segments morph (flex-grow transition) if the data updates
            // eslint-disable-next-line react/no-array-index-key
            key={i}
            className={styles.seg}
            data-active={!o.isOther && selected.has(o.value)}
            data-other={o.isOther || undefined}
            style={{ flexGrow: o.share }}
            title={`${formatOption(o.value)} ${round(o.share * 100)}%`}
          />
        ))}
      </div>
      <div className={styles.legend}>
        {visible.map(o => (
          <span
            key={o.value}
            className={styles.legendItem}
            data-active={!o.isOther && selected.has(o.value)}
            data-other={o.isOther || undefined}
          >
            <span className={styles.legendDot} aria-hidden="true" />
            <span className={styles.legendLabel}>{formatOption(o.value)}</span>
            <span className={styles.legendPct}>{`${round(o.share * 100)}%`}</span>
          </span>
        ))}
        {(hidden > 0 || showAll) && (
          <button type="button" className={styles.legendMore} onClick={() => setShowAll(v => !v)}>
            {showAll ? 'Show less' : `+${hidden} more`}
          </button>
        )}
      </div>
    </>
  )
}

/**
 * One "wide split card": the spec value on the left (icon, label, value, standing pill),
 * its class distribution beside it. Long enum values (multi-valued specs like
 * "Streaming, content creation, vlogging") are clamped to two lines and expand on click;
 * a many-option legend collapses to the top few (see CategoricalChart).
 */
const SpecCard: React.VFC<{
  spec: DealHuntSpec
  dist: SpecDistribution | null
  peersName: string
}> = ({ spec, dist, peersName }) => {
  const [valueOpen, setValueOpen] = useState(false)
  const label = spec.displayName || dist?.label || titleCase(spec.specKey)
  const value = formatSpecValue(spec)
  const pill = dist ? standingPill(dist) : null
  const unit = dist?.kind === 'quantifiable' && dist.unit ? ` ${dist.unit}` : ''
  // Long / multi-valued strings get the clamp+expand treatment; short values stay a plain span.
  const isLong = value.length > 16

  return (
    <div className={styles.card} data-wide={dist ? true : undefined}>
      <div className={styles.cardLeft}>
        <div className={styles.cardHead}>
          <span className={styles.iconWrap}>
            <SpecIcon name={spec.icon} className={styles.icon} />
          </span>
          <span className={styles.cardLabel}>{label}</span>
        </div>
        {isLong ? (
          <button
            type="button"
            className={styles.cardValue}
            data-clamp={valueOpen ? undefined : true}
            data-button
            title={value}
            aria-expanded={valueOpen}
            onClick={() => setValueOpen(o => !o)}
          >
            {value}
          </button>
        ) : (
          <span className={styles.cardValue}>{value}</span>
        )}
        {pill && (
          <span className={styles.pill} data-tone={pill.tone}>
            {pill.label}
          </span>
        )}
      </div>

      {dist && (
        <div className={styles.cardRight}>
          {dist.kind === 'quantifiable' ? (
            <DistributionBars
              distribution={dist.distribution}
              productBucket={dist.productBucket}
              markerText={dist.value !== null ? `${dist.value}${unit}` : null}
              axisMin={`${round(dist.stats.min)}${unit}`}
              axisMax={`${round(dist.stats.max)}${unit}`}
            />
          ) : (
            <CategoricalChart d={dist} />
          )}
          <p className={styles.caption}>{caption(dist, peersName)}</p>
        </div>
      )}
    </div>
  )
}

const KeySpecs: React.VFC<KeySpecsProps> = ({ specs, specDistributions }) => {
  const [showFull, setShowFull] = useState(false)

  const highlights = useMemo(() => specs.filter(s => s.isFeatured).slice(0, 4), [specs])

  // Distributions keyed by specKey for the per-card chart lookup.
  const distByKey = useMemo(() => {
    const map = new Map<string, SpecDistribution>()
    for (const d of specDistributions?.distributions ?? []) {
      map.set(d.specKey, d)
    }
    return map
  }, [specDistributions])

  const peersName = specDistributions?.class
    ? specDistributions.class.name.toLowerCase()
    : 'the class'

  const groups = useMemo(() => {
    const map = new Map<string, DealHuntSpec[]>()
    for (const spec of specs) {
      const key = spec.specCategory || 'other'
      if (!map.has(key)) {
        map.set(key, [])
      }
      map.get(key)!.push(spec)
    }
    return Array.from(map.entries())
  }, [specs])

  if (!specs?.length) {
    return null
  }

  return (
    <section className={styles.section}>
      <SectionHeader title="Key specs" />

      {highlights.length > 0 && (
        <div className={styles.cards}>
          {highlights.map(spec => (
            <SpecCard
              key={spec.specKey}
              spec={spec}
              dist={distByKey.get(spec.specKey) ?? null}
              peersName={peersName}
            />
          ))}
        </div>
      )}

      <button
        type="button"
        className={styles.toggle}
        aria-expanded={showFull}
        onClick={() => setShowFull(v => !v)}
      >
        {showFull ? 'Hide full specifications ▴' : 'View all specifications ▾'}
      </button>

      {/* Always in the DOM, hidden via CSS class — the full spec table must be visible to
          no-JS crawlers (SEO audit C1: only the 3 featured specs were SSR'd, making the
          spec payload invisible to search engines and AI agents alike). A class, not the
          `hidden` attribute: stylesheet `display` rules beat `hidden` (see 7a1d0b14). */}
      <div className={showFull ? styles.groups : styles.groupsCollapsed}>
        {groups.map(([category, items]) => (
          <div key={category} className={styles.group}>
            <div className={styles.groupHead}>
              <span className={styles.groupDot} aria-hidden="true" />
              <h3 className={styles.groupTitle}>{titleCase(category)}</h3>
            </div>
            <dl className={styles.specList}>
              {items.map(spec => (
                <div key={spec.specKey} className={styles.specRow}>
                  <dt className={styles.specLabel}>
                    {spec.displayName || titleCase(spec.specKey)}
                  </dt>
                  <dd className={styles.specValueCell}>{formatSpecValue(spec)}</dd>
                </div>
              ))}
            </dl>
          </div>
        ))}
      </div>
    </section>
  )
}

export default KeySpecs
